import { Link, Navigate } from 'react-router-dom'
import { useAllocationRuns } from '../api/hooks'

/**
 * What the Results Explorer tab lands on: the most recent run there are results to explore.
 *
 * @remarks
 * WHY THIS EXISTS
 *
 * `/results` rendered `<RunConsole />` — the same component as `/runs`, so the two tabs
 * showed the same screen and only `/results/:runId` was ever the Results Explorer. It had
 * been that way since the first commit: the tab needed somewhere to point before there
 * was anything to point at, and the placeholder outlived the reason for it.
 *
 * Two tabs showing one screen is worse than one tab. A reader who clicks the second and
 * gets the first learns that the navigation does not mean what it says, and stops reading
 * it — which costs them the screens that ARE distinct.
 *
 * WHICH RUN, AND WHY NOT SIMPLY THE NEWEST
 *
 * The most recent run that PRODUCED A PERIOD'S RESULTS — which is not the same as the
 * most recent run. A preview never supersedes and is never superseded; it is a rehearsal,
 * so a preview against an old period does not displace a real close of a newer one.
 *
 * A picker was the other option and it would have been the Run Console again: same list,
 * same columns, one fewer button. Somebody opening "Results Explorer" wants results — so
 * land on them, and let the Run Console stay the place where runs are chosen, started and
 * compared. Its history rows already open any run, including previews.
 *
 * WHAT COUNTS AS EXPLORABLE
 *
 * A completed run whose result rows still exist. Superseded runs count: superseding does
 * not make a run wrong, it makes it previous, and comparing this month against what the
 * last run said is a real question.
 *
 * Purged runs do not. Retention ages out `AllocationResult` and deliberately keeps the
 * run header — what it totalled and whether it reconciled — so a purged run is a real row
 * with nothing behind it (ADR 0003). Landing on one would open an empty matrix and blame
 * the reader for it.
 */
export function ResultsLanding() {
  // Every period. The tab is not scoped to one, and the newest results are the answer
  // whichever period they belong to.
  const { data: runs = [], isLoading } = useAllocationRuns(null)

  if (isLoading) {
    return <p className="px-4 py-3 text-[12px]">Loading…</p>
  }

  const explorable = runs
    .filter(
      (run) =>
        (run.status === 'Completed' || run.status === 'Superseded') &&
        !run.resultsPurged &&
        run.resultRowCount > 0,
    )
    /*
        Newest first, by run id.

        Both timestamps are nullable — a run that never started has no start time — so
        ordering on either needs a fallback, and a fallback is a rule about which of two
        unknowns is newer. RunId is an identity column: always present, always increasing,
        and "the most recent run" and "the highest run id" are the same statement.
    */
    .sort((a, b) => b.runId - a.runId)

  /*
      A real run in preference to a preview, and only then the newest.

      A preview never supersedes anything and is never superseded — it is a rehearsal, not
      a result (§5 step 8). So the newest run is not always the one somebody opening
      "Results Explorer" means: a preview run this afternoon against last July does not
      displace January's actual close.

      Previews are still offered when they are all there is, because then they ARE the
      only thing to explore, and that is exactly what a preview is for. The header names
      the run and says 'preview' either way, so the reader is never guessing which they
      are looking at.
  */
  const latest = explorable.find((run) => !run.isPreview) ?? explorable[0]

  if (latest) {
    // `replace`, so the browser's back button returns to wherever the reader came from
    // rather than to this redirect, which would bounce them straight forward again.
    return <Navigate to={`/results/${latest.runId}`} replace />
  }

  return (
    <div className="px-4 py-4 text-[12px]" style={{ color: 'var(--color-muted)' }}>
      <p className="mb-1" style={{ color: 'var(--color-ink)' }}>
        There are no results to explore yet.
      </p>
      <p className="mb-3">
        {runs.length === 0
          ? 'No allocation run has been executed.'
          : /*
                Runs exist but none of them has rows to show. Saying which of the two
                reasons it is matters: "nothing ran" and "what ran was aged out" lead to
                completely different next actions, and a single vague sentence would send
                half the readers the wrong way.
            */
            'Every run either did not complete, or its results have been aged out by retention. ' +
            'The run headers remain on the Run Console with what they totalled and whether they reconciled.'}
      </p>
      <Link to="/runs" style={{ color: 'var(--color-accent)' }}>
        Go to the Run Console
      </Link>
    </div>
  )
}
